// =============================================================================
// EnTetePage.js — l'en-tête COMPACT des pages de travail (lot L4 de la refonte
// « Mosaïque vivante » ; synthèse §6.1 et §8).
// =============================================================================
// Une page de travail n'a plus de bannière : un fil « Enio / Module / Page »
// (13 px, barre oblique teal), une tuile PLEINE de 40 px, le titre en
// Montserrat Light (--fs-page : 32 px, 26 au téléphone), la ligne d'état en
// dessous, les actions à droite. Environ 75 px de haut : le premier contenu
// utile remonte d'une centaine de pixels (mesures dans le plan L4).
// La plaque navy, elle, est réservée aux accueils de module (PlaqueModule.js).
//
// C'est aussi le rendu de <ModuleHero> (uxComponents.js) depuis L4 : ses 27
// appelants changent d'un coup, sans changer d'API.
//   module  : clé de navigation (athos, gouv, …) ou alias historique (agora,
//             admin, veille…) — cleModule() de navConstants ;
//   teinte  : teinte de la tuile quand ce n'est pas celle du module (instance
//             de Gouvernance : AG bleu, CA teal, CODIR violet, Bureau orange) ;
//   icone   : clé de src/icones.js ; un emoji ou une clé inconnue est ignoré
//             (charte : zéro emoji), la tuile porte alors l'icône du module.
// Le fil est du TEXTE en L4 : il deviendra une vraie navigation quand il
// montera dans l'en-tête de la coquille (lot L6). Au téléphone, il est masqué :
// le bouton de sommaire du module (ModuleSidebar) joue déjà ce rôle.
// Styles : section « coquille » de src/feuilleGlobale.js (CSS_COQUILLE).
// =============================================================================
import { lazy, Suspense } from "react";
import { NAV_COLORS, NAV_ICONES, NAV_LIBELLES, LIBELLES_ADMIN, cleModule } from "./navConstants";
import { Tuile, estIcone } from "./icones";
import { useRepereEspace } from "./repereEspaceContexte";

// Le RENDU du bandeau de commande vit dans src/bandeauCommande.js, un morceau
// PARESSEUX (il voyage avec les pages, jamais dans le bundle principal) qui
// s'enregistre ici à son chargement. Une page migrée l'importe : le bandeau
// est alors rendu tout de suite ; sinon il est chargé à la demande, l'en-tête
// compact tenant la place le temps du chargement.
let rendreBandeau = null;
/** Appelé par src/bandeauCommande.js à son chargement. */
export function enregistrerBandeau(composant) { rendreBandeau = composant; }
const BandeauDiffere = lazy(() => import("./bandeauCommande"));

// Libellé du fil quand l'alias ne désigne pas un module de navigation :
// Administration et Super administration (modules du rail, lot L11r),
// libellés de la source unique navConstants.
// module="moi" : écran PERSONNEL, hors module (notes de frais, espace salarié,
// lot L11f) : le fil ne porte que le nom de la page, comme celui de la
// coquille (LIBELLES_PERSONNELS), et la tuile est navy (teinte de Pilotage).
// Repère d'espace (26/10/2026) : dans un module cloisonné par mutuelle
// (Conformité, Ma mutuelle), le module fournit son repère par
// RepereEspaceContexte ; il se pose sous la ligne d'état et son complément
// (`complementEtat`), avant les enfants. Les pages RH posent le leur
// elles-mêmes (<LigneEspaceRH/>, src/rhEspace.js).

// ── Le BANDEAU DE COMMANDE (décision de Pierre du 27/10/2026) ──────────────
// Une page MIGRÉE (src/grammaireEnTetes.test.js, MIGRES_GRAMMAIRE) passe des
// emplacements TYPÉS au lieu de l'emplacement libre `actions` :
//   principale   UNE action { libelle, icone, onClick, disabled, titre, nom } :
//                le geste qui crée le contenu de la page, en aplat navy, collé
//                au bord droit ; pleine largeur sous le titre au téléphone ;
//   secondaires  DEUX actions au plus, en contour (le surplus passe dans
//                « Plus ») ; au téléphone, elles vont dans le menu ;
//   plus         { creer, importer, exporter, diffuser, organiser } : listes
//                d'actions par intention (ordre fixe, bandeauCommande.js) ;
//   reglages     le CONTENU du panneau latéral des réglages (SectionReglages,
//                LigneReglage, ZoneSensible de src/PanneauReglages.js), ouvert
//                par la roue dentée ; visible des seuls administrateurs de la
//                session RÉELLE (sessionReelleContexte.js) ; titreReglages
//                nomme le panneau. Une FONCTION ({ fermer }) => contenu quand
//                une ligne ouvre sa propre fenêtre (ou une autre page) : elle
//                ferme d'abord le panneau, une seule fenêtre à la fois ;
//                notePlus : une phrase sous le menu « Plus » (au bureau, quand
//                la roue est là : « … se règlent par la roue dentée ») ;
//                droitReglages : le droit RÉEL des réglages portés (Q4, Pierre
//                le 27/10/2026), un booléen TOUJOURS relu par le serveur pour
//                la session réelle (estAdmin réel, rhWhoAmI…), jamais « Voir
//                comme » ni un champ de fiche lu dans le navigateur ; absent,
//                les administrateurs de la session réelle, à l'identique ;
//                sousTitreReglages dit alors à qui le panneau est réservé ;
//   chiffres     les pastilles de la ligne d'état [{ valeur, libelle, attente }] ;
//                `onClick` (et `nom`) : la pastille OUVRE ce qu'elle compte
//                (« 2 non lues » de Mes notes de frais), jamais une page ;
//   onglets      la rangée <Onglets> de la page, rendue au PIED de la plaque ;
//   aide         l'explication longue repliée sous le bandeau { titre,
//                resume, contenu } ;
//   retour       le LIEN DE RETOUR d'une sous-page sans route { libelle,
//                onClick, titre }, posé au-dessus de la plaque, sous le fil de
//                la coquille (R4 : le retour n'est jamais un bouton du bandeau) ;
//   vignette     (avec `bandeau`) la variante photo de la tuile : un nœud
//                (la photo libre d'un parlementaire, ses initiales à défaut)
//                rendu à la place et à la taille de la tuile (lot 3,
//                02/11/2026) ; son crédit se dit dans les enfants ;
//   titreSousTuile (avec `bandeau`) au téléphone, un titre LONG (au-delà de
//                TITRE_LONG signes : l'intitulé d'un dossier législatif) passe
//                sous la tuile, sur toute la largeur de la plaque ; la page le
//                demande, aucune autre ne bouge (revue du lot 3, 02/11/2026) ;
//   complementEtat ce qui COMPLÈTE la ligne d'état, à sa suite et avant le
//                repère d'espace (la criticité d'un constat de l'audit, à son
//                code couleur) ; les enfants, eux, suivent le repère (une
//                note, un lien vers une autre page : version intégrée v1240).
// Une action peut être une BASCULE (`presse` : booléen) : aria-pressed sur
// son bouton, case cochable dans le menu « Plus » du téléphone (« Suivre cet
// amendement », lot 3).
// Dans `plus`, `danger` (hors des intentions) range en DERNIER, séparé, le
// geste qui détruit l'objet d'un propriétaire qui n'a pas la roue (R16) : il
// n'est rouge que dans sa confirmation.
// Sans aucun de ces emplacements (ni `bandeau`), le rendu est l'en-tête
// compact d'avant, à l'identique : les pages se migrent module par module.
// Tant qu'une page migre, `actions` reste accepté (rendu avant « Plus »).
// Le rendu et les styles du bandeau : src/bandeauCommande.js (paresseux).
const EMPLACEMENTS = ["principale", "secondaires", "plus", "reglages", "chiffres", "onglets", "aide", "retour"];
const present = (v) => v !== undefined && v !== null && v !== false && !(Array.isArray(v) && !v.filter(Boolean).length);

export default function EnTetePage(props) {
  const { module = "pilotage", titre, etat, actions, teinte, icone, children, complementEtat = null, bandeau = false } = props;
  const cle = cleModule(module);
  const libelle = module === "moi" ? null : (LIBELLES_ADMIN[module] || NAV_LIBELLES[cle]);
  const courant = typeof titre === "string" && titre !== libelle ? titre : null;
  const repere = useRepereEspace();
  const compact = (
    <header className="entete-page" data-entete-page={cle}>
      <p className="entete-fil">
        <span>Enio</span>
        {libelle && <span className="entete-sep" aria-hidden="true">/</span>}
        {libelle && <span>{libelle}</span>}
        {courant && <span className="entete-sep" aria-hidden="true">/</span>}
        {courant && <span className="entete-cur">{courant}</span>}
      </p>
      <div className="entete-ligne">
        <Tuile couleur={teinte || NAV_COLORS[cle]} taille={40}
          icone={estIcone(icone) ? icone : NAV_ICONES[cle]} variante="pleine" data-vt-tuile={cle} />
        <div className="entete-texte">
          <h1 className="entete-titre">{titre}</h1>
          {etat && <div className="entete-etat">{etat}</div>}
          {complementEtat}
          {repere}
          {children}
        </div>
        {actions && <div className="entete-actions">{actions}</div>}
      </div>
    </header>
  );
  if (!bandeau && !EMPLACEMENTS.some((k) => present(props[k]))) return compact;
  const Bandeau = rendreBandeau;
  if (Bandeau) return <Bandeau {...props} cle={cle} repere={repere} />;
  return <Suspense fallback={compact}><BandeauDiffere {...props} cle={cle} repere={repere} /></Suspense>;
}
